/**
 * FormFlu Help & Support Page
 * 
 * Documentation, guides, FAQ, and support information.
 */

import { useState } from 'react';
import { DashboardLayout } from '../../components/dashboard';
import './HelpPage.css';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: 'How do I create my first form?',
    answer: 'Navigate to the Forms page and click "Create Form". Give your form a name, then use the visual builder to add steps and fields. Once you\'re done, publish your form to make it live.'
  },
  {
    question: 'How do I embed a form on my website?',
    answer: 'After publishing your form, click the "Embed" button to get the embed code. Copy the script tag and web component tag, then paste them into your website\'s HTML where you want the form to appear.'
  },
  {
    question: 'Is my data HIPAA compliant?',
    answer: 'Yes! FormFlu is built from the ground up with HIPAA compliance in mind. All PHI (Protected Health Information) is encrypted at rest and in transit, stored in isolated tenant databases, and requires MFA verification to access.'
  },
  {
    question: 'How do I view form submissions?',
    answer: 'Go to your form and click the "Submissions" tab. You\'ll see a list of all submissions. To view the actual data (which may contain PHI), you\'ll need to verify with your MFA code.'
  },
  {
    question: 'Can I set up A/B testing for my forms?',
    answer: 'Yes! FormFlu supports A/B testing through variants. When building your form, you can create multiple variants of each step and set traffic allocation percentages. The analytics page will show you which variant performs best.'
  },
  {
    question: 'How do I invite team members?',
    answer: 'Go to Team in the sidebar, then click "Invite Member". Enter their email and select a role. They\'ll receive an invitation email to join your organization.'
  },
];

export function HelpPage() {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout title="Help & Support">
      <div className="help-page">
        {/* Hero Section */}
        <section className="help-hero">
          <h1>How can we help you?</h1>
          <p>Search our documentation or browse the topics below</p>
          <div className="help-search">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search for help..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </section>

        {/* Quick Links */}
        <section className="quick-links">
          <h2>Getting Started</h2>
          <div className="links-grid">
            <div className="link-card">
              <span className="link-icon">📝</span>
              <h3>Create Your First Form</h3>
              <p>Learn how to build and publish a form in minutes</p>
              <a href="#" className="card-link">Read guide →</a>
            </div>
            <div className="link-card">
              <span className="link-icon">🔗</span>
              <h3>Embed on Your Website</h3>
              <p>Add FormFlu forms to any website with one line of code</p>
              <a href="#" className="card-link">Read guide →</a>
            </div>
            <div className="link-card">
              <span className="link-icon">📊</span>
              <h3>Analytics & A/B Testing</h3>
              <p>Optimize your forms with data-driven insights</p>
              <a href="#" className="card-link">Read guide →</a>
            </div>
            <div className="link-card">
              <span className="link-icon">🔒</span>
              <h3>HIPAA Compliance</h3>
              <p>Understand how FormFlu protects sensitive data</p>
              <a href="#" className="card-link">Read guide →</a>
            </div>
          </div>
        </section>

        {/* Video Tutorials */}
        <section className="video-section">
          <h2>Video Tutorials</h2>
          <div className="videos-grid">
            <div className="video-card">
              <div className="video-placeholder">
                <span className="play-icon">▶️</span>
              </div>
              <h4>Introduction to FormFlu</h4>
              <span className="video-duration">3:45</span>
            </div>
            <div className="video-card">
              <div className="video-placeholder">
                <span className="play-icon">▶️</span>
              </div>
              <h4>Building Multi-Step Forms</h4>
              <span className="video-duration">5:20</span>
            </div>
            <div className="video-card">
              <div className="video-placeholder">
                <span className="play-icon">▶️</span>
              </div>
              <h4>Setting Up A/B Tests</h4>
              <span className="video-duration">4:15</span>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq-section">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-list">
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, index) => (
                <div 
                  key={index} 
                  className={`faq-item ${expandedFaq === index ? 'expanded' : ''}`}
                >
                  <button 
                    className="faq-question"
                    onClick={() => toggleFaq(index)}
                  >
                    <span>{faq.question}</span>
                    <span className="faq-toggle">{expandedFaq === index ? '−' : '+'}</span>
                  </button>
                  {expandedFaq === index && (
                    <div className="faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="no-results">
                <p>No results found for "{searchQuery}"</p>
                <button onClick={() => setSearchQuery('')}>Clear search</button>
              </div>
            )}
          </div>
        </section>

        {/* Contact Support */}
        <section className="contact-section">
          <div className="contact-card">
            <div className="contact-content">
              <span className="contact-icon">💬</span>
              <h3>Still need help?</h3>
              <p>Our support team is here to help you with any questions.</p>
            </div>
            <div className="contact-actions">
              <a href="mailto:support@formflu.app" className="btn btn-primary">
                Contact Support
              </a>
              <span className="response-time">Usually responds within 24 hours</span>
            </div>
          </div>
        </section>

        {/* Additional Resources */}
        <section className="resources-section">
          <h2>Additional Resources</h2>
          <div className="resources-grid">
            <a href="#" className="resource-item">
              <span className="resource-icon">📚</span>
              <div>
                <h4>API Documentation</h4>
                <p>Integrate FormFlu with your applications</p>
              </div>
            </a>
            <a href="#" className="resource-item">
              <span className="resource-icon">🔄</span>
              <div>
                <h4>Changelog</h4>
                <p>See what's new in FormFlu</p>
              </div>
            </a>
            <a href="#" className="resource-item">
              <span className="resource-icon">🛡️</span>
              <div>
                <h4>Security & Privacy</h4>
                <p>Learn about our security practices</p>
              </div>
            </a>
            <a href="#" className="resource-item">
              <span className="resource-icon">📋</span>
              <div>
                <h4>Terms of Service</h4>
                <p>Review our terms and conditions</p>
              </div>
            </a>
          </div>
        </section>

        {/* System Status */}
        <section className="status-section">
          <div className="status-card">
            <div className="status-indicator">
              <span className="status-dot operational"></span>
              <span>All Systems Operational</span>
            </div>
            <a href="#" className="status-link">View status page →</a>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}

export default HelpPage;
